import React, { useState, useEffect, useRef } from 'react';
import FieldError from "../FieldError/FieldError";
import SuggestionBox from "../SuggestionBox/SuggestionBox";
import styles from "./JobLocationFields.module.scss";
import Select from 'react-select';

function JobLocationFields({ form, type, whichForm, hintText, locationPlaceholder }) {
    const [suggestionVisibility, setSuggestionVisibility] = useState({});
    const [locations, setLocations] = useState([{ id: 0, value: '', locationId: null }]);
    // Check if component is mounted
    const isMounted = useRef(true);

    // Set initial distance value based on form values
    const distance = [
        { value: 0, label: '0 miles' },
        { value: 1, label: '1 mile' },
        { value: 3, label: '3 miles' },
        { value: 5, label: '5 miles' },
        { value: 10, label: '10 miles' },
        { value: 15, label: '15 miles' },
        { value: 20, label: '20 miles' },
        { value: 30, label: '30 miles' },
        { value: 50, label: '50 miles' },
    ];

    const [selectedDistance, setSelectedDistance] = useState(
        distance.find(d => d.value === form.values.distance) || distance[0]
    );

    useEffect(() => {
        const matchedOption = distance.find(d => d.value === form.values.distance);
        if (matchedOption) {
            setSelectedDistance(matchedOption);
        }
    }, [form.values.distance]);

    //Helper function to update Formik's location-related fields
    const updateFormikLocations = (locationsArray) => {
        form.setFieldValue('locations', locationsArray.map(loc => loc.locationId || loc.value));
        form.setFieldValue('locationNames', locationsArray.map(loc => loc.value));
    };

    // Initialize locations from Formik values when component mounts
    useEffect(() => {
        isMounted.current = true;

        if (form.values.locations && form.values.locations.length > 0) {
            const initialLocations = form.values.locations.map((loc, index) => {
                if (typeof loc === 'string') {
                    return { id: index, value: loc, locationId: null };
                } else if (typeof loc === 'number') {
                    return { id: index, value: form.values.locationNames?.[index] || '', locationId: loc };
                } else if (loc && typeof loc === 'object') {
                    return { id: index, value: loc.name || '', locationId: loc.id };
                }
                return { id: index, value: '', locationId: null };
            });

            if (isMounted.current) {
                setLocations(initialLocations);
            }
        }

        return () => {
            isMounted.current = false;
        };
    }, [form.values.locations, form.values.locationNames]);

    // Set initial distance value if available
    const handleDistanceChange = (selectedOption) => {
        setSelectedDistance(selectedOption);
        form.setFieldValue('distance', selectedOption.value);
    };

    // Handle location change for both job alert and job post forms
    const handleLocationChange = (id, value) => {
        if (!isMounted.current) return;

        const updatedLocations = locations.map(location =>
            location.id === id ? { ...location, value, locationId: null } : location
        );

        setLocations(updatedLocations);
        updateFormikLocations(updatedLocations);
        setSuggestionVisibility(prev => ({ ...prev, [id]: true }));
    };

    // Handle suggestion selection for job post form
    const handleSuggestionSelect = (id, suggestion) => {
        if (!isMounted.current) return;

        const updatedLocations = locations.map(location =>
            location.id === id ? { ...location, value: suggestion.name, locationId: suggestion.id } : location
        );

        setLocations(updatedLocations);
        updateFormikLocations(updatedLocations);
        setSuggestionVisibility(prev => ({ ...prev, [id]: false }));
    };

    // Add a new location field for job post form
    const addLocationField = () => {
        if (!isMounted.current) return;

        const newId = locations.length > 0 ? Math.max(...locations.map(l => l.id)) + 1 : 0;
        const newLocations = [...locations, { id: newId, value: '', locationId: null }];

        setLocations(newLocations);
        form.setFieldValue('locations', newLocations.map(loc => loc.locationId || loc.value));
        form.setFieldValue('locationNames', newLocations.map(loc => loc.value));
    };

    // Remove a location field for job post form
    const removeLocationField = (id) => {
        if (!isMounted.current) return;

        const updatedLocations = locations.filter(location => location.id !== id);
        setLocations(updatedLocations);
        updateFormikLocations(updatedLocations);

        setSuggestionVisibility(prev => {
            const newVisibility = { ...prev };
            delete newVisibility[id];
            return newVisibility;
        });
    };

    return (
        <section className="jobLocFieldsWrap fieldGroupWrap">
            <div className="row">
                <div className="fieldHead col-md-3">
                    <h2>Job location</h2>
                </div>
                <div className="jobFields col-md-9">
                    <div className="row">
                        <div className="col-md-12">
                            {whichForm !== 'jobAlertForm' &&
                                <div className="fieldWrap">
                                    <div className={`${styles.labelBox}`}>
                                        <input
                                            className={styles.checkbox}
                                            type="checkbox"
                                            name="is_remote_job"
                                            checked={form.values.is_remote_job}
                                            onChange={form.handleChange}
                                        />
                                        <label
                                            className={styles.label}
                                            htmlFor="jobAddress"
                                        >
                                            This is a remote job
                                        </label>
                                    </div>
                                </div>
                            }
                            {whichForm === 'jobAlertForm' ? (
                                <div className="fieldWrap">
                                    <div className="labelBox">
                                        <label htmlFor="jobAddress">Address</label>
                                    </div>
                                    <input
                                        id="location"
                                        type="text"
                                        name="location"
                                        placeholder={locationPlaceholder}
                                        value={form.values.location || ""}
                                        onChange={(e) => {
                                            form.setFieldValue('location', e.target.value);
                                            setSuggestionVisibility(true);
                                        }}
                                        onFocus={() => setSuggestionVisibility(true)}
                                        autoComplete="off"
                                    />
                                    {form.values.location && (
                                        <SuggestionBox
                                            keyword={form.values.location}
                                            name="location"
                                            form={form}
                                            visibilty={suggestionVisibility}
                                            setVisibility={setSuggestionVisibility}
                                            onSuggestionSelect={(suggestion) => {
                                                form.setFieldValue('location', suggestion);
                                                setSuggestionVisibility(false);
                                            }}
                                        />
                                    )}
                                    <FieldError error={form.errors.location} touched={form.touched.location} />
                                </div>
                            ) : (
                                <>
                                    {locations.map((location, index) => {
                                        return (
                                            <div className="fieldWrap" key={location.id}>
                                                <div className="labelBox">
                                                    <label htmlFor={`location-${index}`}>Address {index + 1}</label>
                                                </div>
                                                <input
                                                    id={`location-${index}`}
                                                    type="text"
                                                    name={`location-${index}`}
                                                    placeholder={locationPlaceholder}
                                                    value={location.value || ""}
                                                    onChange={(e) => handleLocationChange(location.id, e.target.value)}
                                                    onFocus={() => setSuggestionVisibility(prev => ({ ...prev, [location.id]: true }))}
                                                    autoComplete="off"
                                                />
                                                {location.value && (
                                                    <SuggestionBox
                                                        keyword={location.value}
                                                        form={form}
                                                        visibilty={suggestionVisibility[location.id] || false}
                                                        setVisibility={(isVisible) =>
                                                            setSuggestionVisibility((prev) => ({ ...prev, [location.id]: isVisible }))
                                                        }
                                                        onSuggestionSelect={(suggestion) => handleSuggestionSelect(location.id, suggestion)}
                                                        name="locations"
                                                    />
                                                )}
                                                <FieldError error={form.errors[`location-${index}`]} touched={form.touched[`location-${index}`]} />
                                                {index > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeLocationField(location.id)}
                                                        className={styles.removeLocationButton}
                                                    >
                                                        Remove
                                                    </button>
                                                )}
                                            </div>
                                        )
                                    })}
                                    <button type="button" onClick={addLocationField} className={styles.addLocationButton}>
                                        Add More Location
                                    </button>
                                </>
                            )}
                        </div>
                        <div className="col-md-6">
                            {whichForm === 'jobAlertForm' &&
                                <div className="fieldWrap">
                                    <label htmlFor="distance">Distance</label>
                                    <Select
                                        isClearable={false}
                                        isSearchable={false}
                                        options={distance}
                                        value={selectedDistance}
                                        onChange={handleDistanceChange}
                                    />
                                </div>
                            }
                        </div>
                        {whichForm === 'jobAlertForm' &&
                            <div className={styles.wfh}>
                                <div className={styles.checkboxWrapper}>
                                    <input
                                        type="checkbox"
                                        id="work_from_home"
                                        name="work_from_home"
                                        checked={form.values.work_from_home}
                                        onChange={event => form.setFieldValue('work_from_home', event.target.checked)}
                                    />
                                </div>
                                <div className={styles.checkboxTitle}>
                                    <h4 htmlFor="work_from_home">Include work from home jobs?</h4>
                                </div>
                            </div>
                        }
                        {hintText &&
                            <div className={styles.locationLabelBox}>
                                <label htmlFor="jobAddress">Postcode*</label>
                                <div dangerouslySetInnerHTML={{ __html: hintText }} />
                            </div>
                        }
                    </div>
                </div>
            </div>
        </section>
    );
}

export default JobLocationFields;